import {$$} from '@vue-macros/reactivity-transform/macros'

import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'

import {
  IMarkdownPropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/MarkdownPropertyTypeStrategy/types/IMarkdownPropertyTypeInterfaces'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'

export function useMarkdownValueTypeStrategy() {
  const strategyComponentType = PropertyStrategyComponentType.Dropdown

  const fullValueToText = (
    value: IMarkdownPropertyTypeInterfaces['fullValue'],
    property: IDatabasePropertyResponseInstance,
  ): string => {
    if (!value) return ''

    const parser = new DOMParser()
    const doc = parser.parseFromString(String(value), 'text/html')

    const lines: string[] = []

    const walk = (node: Node, indent = 0, listIndex = 1): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim()
        if (text) lines.push(' '.repeat(indent) + text)
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return

      const el = node as HTMLElement
      const tag = el.tagName.toLowerCase()

      switch (tag) {
      case 'h1':
        lines.push(`# ${el.textContent?.trim()}`)
        lines.push('')
        break
      case 'h2':
        lines.push(`## ${el.textContent?.trim()}`)
        lines.push('')
        break
      case 'h3':
        lines.push(`### ${el.textContent?.trim()}`)
        lines.push('')
        break
      case 'p':
        lines.push(el.textContent?.trim() || '')
        lines.push('')
        break
      case 'ol':
        Array.from(el.children).forEach((li, i) => {
          walk(li, indent, i + 1)
        })
        lines.push('')
        break
      case 'ul':
        Array.from(el.children).forEach((li) => {
          walk(li, indent)
        })
        lines.push('')
        break
      case 'li':
        const isOrdered = el.parentElement?.tagName === 'OL'
        const prefix = isOrdered ? `${listIndex}. ` : '- '
        const liText = el.textContent?.trim() || ''
        lines.push(' '.repeat(indent) + prefix + liText)
        break
      case 'i':
        lines.push('*' + el.textContent?.trim() + '*')
        break
      case 'b':
      case 'strong':
        lines.push('**' + el.textContent?.trim() + '**')
        break
      case 'br':
        lines.push('')
        break
      default:
        Array.from(el.childNodes).forEach((child) => walk(child, indent))
      }
    }

    Array.from(doc.body.childNodes).forEach((node) => walk(node))

    return lines
      .map(line => line.trimEnd())
      .filter((line, i, arr) => !(line === '' && arr[i - 1] === ''))
      .join('\n')
  }

  const searchToFilterQuery = (search: string, databaseProperties: IDatabasePropertyResponseInstance[]): IFilter => {
    return {
      type: FilterItemType.Group,
      operator: FilterGroupOperator.Or,
      items: databaseProperties.map(databaseProperty => ({
        type: FilterItemType.Property,
        id: databaseProperty.id,
        subProperty: null,
        operator: {
          type: FilterPropertyOperator.Contains,
        },
        payload: {
          type: FilterPropertyPayloadType.Static,
          value: search,
        },
        isDisabled: false,
      })),
    }
  }

  const compareItemValues = (
    valueA: IMarkdownPropertyTypeInterfaces['fullValue'],
    valueB: IMarkdownPropertyTypeInterfaces['fullValue'],
    sortDirection: '1' | '-1',
  ): number => {
    const hasValueA = valueA !== null && valueA !== undefined && valueA !== ''
    const hasValueB = valueB !== null && valueB !== undefined && valueB !== ''
    
    if (!hasValueA && !hasValueB) return 0
    if (!hasValueA) return 1
    if (!hasValueB) return -1
    
    const strA = String(valueA || '')
    const strB = String(valueB || '')
    const comparison = strA.localeCompare(strB)
    
    return sortDirection === '1' ? comparison : -comparison
  }

  return $$({
    fullValueToText,
    strategyComponentType,
    searchToFilterQuery,
    compareItemValues,
  })
}
